/**
 * Button Component - Constants
 * ============================
 * Tipos y mapas de estilo del botón. Los colores salen de los tokens
 * migrados del sistema Figma (sin tokens nuevos). soft/ghost usan opacity
 * modifiers sobre los canales RGB de cada token.
 */

import { getIcon } from '../../icons';

// ============================================================================
// TYPES
// ============================================================================

export type ButtonIntent =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'success'
  | 'warning'
  | 'info'
  | 'caution'
  | 'prime'
  | 'prime-alt';

export type ButtonEmphasis = 'filled' | 'soft' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonShape = 'rounded' | 'pill';
export type ButtonType = 'button' | 'submit' | 'reset';
export type ButtonIconType =
  | 'none'
  | 'star'
  | 'chat'
  | 'check'
  | 'arrow-right'
  | 'plus'
  | 'search'
  | 'download'
  | 'trash';

export interface SizeStyles {
  container: string;
  icon: string;
  gap: string;
  label: string;
  subtitle: string;
}

export interface DefaultValues {
  intent: ButtonIntent;
  emphasis: ButtonEmphasis;
  size: ButtonSize;
  shape: ButtonShape;
  type: ButtonType;
}

// ============================================================================
// INTENT × EMPHASIS
// ============================================================================

export const INTENT_EMPHASIS: Record<ButtonIntent, Record<ButtonEmphasis, string>> = {
  primary: {
    filled: 'ec-bg-color-primary ec-text-color-primary-contrast hover:ec-bg-color-primary-dark',
    soft: 'ec-bg-color-primary-light ec-text-color-primary-dark hover:ec-brightness-95',
    ghost: 'ec-bg-transparent ec-text-color-primary hover:ec-bg-color-primary-light'
  },
  secondary: {
    filled: 'ec-bg-gray-dark ec-text-white hover:ec-bg-black',
    soft: 'ec-bg-gray-extralight ec-text-gray-dark hover:ec-bg-gray-light',
    ghost: 'ec-bg-transparent ec-text-gray-dark hover:ec-bg-gray-extralight'
  },
  danger: {
    filled: 'ec-bg-danger ec-text-white hover:ec-bg-danger/90',
    soft: 'ec-bg-danger/12 ec-text-danger hover:ec-bg-danger/20',
    ghost: 'ec-bg-transparent ec-text-danger hover:ec-bg-danger/10'
  },
  success: {
    filled: 'ec-bg-success ec-text-white hover:ec-bg-success/90',
    soft: 'ec-bg-success/12 ec-text-success hover:ec-bg-success/20',
    ghost: 'ec-bg-transparent ec-text-success hover:ec-bg-success/10'
  },
  warning: {
    filled: 'ec-bg-warning ec-text-white hover:ec-bg-warning/90',
    soft: 'ec-bg-warning/12 ec-text-warning hover:ec-bg-warning/20',
    ghost: 'ec-bg-transparent ec-text-warning hover:ec-bg-warning/10'
  },
  info: {
    filled: 'ec-bg-info ec-text-white hover:ec-bg-info/90',
    soft: 'ec-bg-info/12 ec-text-info hover:ec-bg-info/20',
    ghost: 'ec-bg-transparent ec-text-info hover:ec-bg-info/10'
  },
  caution: {
    filled: 'ec-bg-caution ec-text-white hover:ec-bg-caution/90',
    soft: 'ec-bg-caution/12 ec-text-caution hover:ec-bg-caution/20',
    ghost: 'ec-bg-transparent ec-text-caution hover:ec-bg-caution/10'
  },
  prime: {
    filled: 'ec-bg-prime ec-text-white hover:ec-bg-prime-dark',
    soft: 'ec-bg-prime/12 ec-text-prime hover:ec-bg-prime/20',
    ghost: 'ec-bg-transparent ec-text-prime hover:ec-bg-prime/10'
  },
  'prime-alt': {
    filled: 'ec-bg-prime-alt ec-text-white hover:ec-bg-prime-alt-dark',
    soft: 'ec-bg-prime-alt/12 ec-text-prime-alt hover:ec-bg-prime-alt/20',
    ghost: 'ec-bg-transparent ec-text-prime-alt hover:ec-bg-prime-alt/10'
  }
};

// Ring de focus por intent
export const INTENT_RING: Record<ButtonIntent, string> = {
  primary: 'focus-visible:ec-ring-color-primary',
  secondary: 'focus-visible:ec-ring-gray',
  danger: 'focus-visible:ec-ring-danger',
  success: 'focus-visible:ec-ring-success',
  warning: 'focus-visible:ec-ring-warning',
  info: 'focus-visible:ec-ring-info',
  caution: 'focus-visible:ec-ring-caution',
  prime: 'focus-visible:ec-ring-prime',
  'prime-alt': 'focus-visible:ec-ring-prime-alt'
};

// ============================================================================
// SIZES / SHAPES / BASE
// ============================================================================

export const SIZES: Record<ButtonSize, SizeStyles> = {
  sm: { container: 'ec-px-3 ec-py-2', icon: 'ec-w-4 ec-h-4', gap: 'ec-gap-2', label: 'ec-text-sm', subtitle: 'ec-text-xs' },
  md: { container: 'ec-px-4 ec-py-3', icon: 'ec-w-5 ec-h-5', gap: 'ec-gap-2', label: 'ec-text-sm', subtitle: 'ec-text-xs' },
  lg: { container: 'ec-px-6 ec-py-4', icon: 'ec-w-6 ec-h-6', gap: 'ec-gap-3', label: 'ec-text-base', subtitle: 'ec-text-sm' }
};

export const SHAPES: Record<ButtonShape, string> = {
  rounded: 'ec-rounded-lg',
  pill: 'ec-rounded-full'
};

export const BASE_CLASSES = {
  button:
    'ec-inline-flex ec-items-center ec-justify-center ec-font-semibold ec-transition-colors ec-cursor-pointer ' +
    'focus-visible:ec-outline-none focus-visible:ec-ring-2 focus-visible:ec-ring-offset-2',
  disabled: 'ec-bg-gray-light ec-text-gray ec-cursor-not-allowed ec-pointer-events-none',
  block: 'ec-w-full'
};

// ============================================================================
// ICONS
// ============================================================================

/**
 * Iconos SVG predefinidos
 *
 * Las keys públicas (icon="star", etc.) se mantienen igual — cero breaking.
 * Los valores vienen del registro central (`src/icons`); `download` no tiene
 * equivalente en Figma y usa su SVG legacy vía `getIcon()`.
 */
export const ICONS: Record<ButtonIconType, string> = {
  none: '',
  star: getIcon('star') || '',
  chat: getIcon('chat') || '',
  check: getIcon('check') || '',
  'arrow-right': getIcon('arrow-right') || '',
  plus: getIcon('plus') || '',
  search: getIcon('search') || '',
  download: getIcon('download') || '',
  trash: getIcon('trash') || ''
};

export const SPINNER_SVG: string =
  `<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="3" stroke-opacity="0.25"/><path d="M21 12a9 9 0 00-9-9" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>`;

// ============================================================================
// DEFAULTS
// ============================================================================

export const DEFAULTS: DefaultValues = {
  intent: 'primary',
  emphasis: 'filled',
  size: 'md',
  shape: 'rounded',
  type: 'button'
};
